import React from 'react';
import {
    initiales, teinteAvatar, badgeStatut, badgeRegime, rangSurMax
} from '../services/etudiantsAffichage';
import PhotoEtudiant from './PhotoEtudiant';

/**
 * ONGLET 1 — LA LISTE
 * ===================
 * Spec : doc/modules/etudiants.md §Onglet 1 · maquette `etudiants_maquette_v5.html`
 * Lot 1.3. Composant de PRÉSENTATION : il ne charge rien et n'écrit rien. Les données,
 * les filtres et la sélection appartiennent à `Etudiants.js`, qui a besoin du compte
 * pour le badge de sa topbar — deux propriétaires du même nombre en auraient fait deux.
 *
 * ── SEPT COLONNES (écart E2, validé par Bertrand le 2026-08-14 ; les deux rangs
 *    ajoutés le 2026-09-10) ──────────────────────────────────────────────────
 * La maquette en montre trois, la spec neuf. Trois ne suffisent pas : ses propres
 * filtres portent sur le régime et sur le boursier, que sa liste n'affiche pas — on
 * filtrerait sur une information invisible. D'où : identité compacte, Formation,
 * Régime, Boursier, Statut — puis **Rang promo** et **Rang adm.**, demandés « à
 * droite de Statut » le 2026-09-10.
 *
 * ⚠️ Neuf colonnes ne tenaient pas dans un panneau à 35 % de la largeur, et SEPT n'y
 * tiennent qu'au prix de quatre mesures mesurées à l'image : en-tête sur deux lignes,
 * semestre empilé sous le rang, barre sans espaces, colonne d'identité rétrécie de
 * 42 % à 30 %. Le détail et le pourquoi sont dans `.etu-col-rang` (Etudiants.css).
 *
 * Le badge « Boursier » garde la FORME que la maquette lui donne (pastille ambre), il
 * change seulement de colonne.
 *
 * ── QUATRE ÉTATS VIDES, ET C'EST LE POINT DÉLICAT ────────────────────────────
 * « Aucun étudiant » ne veut pas dire la même chose selon la cause, et une seule
 * phrase pour les quatre ferait chercher au mauvais endroit :
 *   · chargement en cours ;
 *   · le serveur a refusé (le message du serveur est relayé tel quel) ;
 *   · l'ANNÉE choisie n'a aucun inscrit → on NOMME les années qui en ont, sinon
 *     l'écran se lit comme « l'import n'a rien écrit » ;
 *   · les FILTRES ne laissent rien passer → on donne le total de l'année et un
 *     bouton pour les retirer.
 */
function EtudiantsListe({
    lignes, total, totalAnnee, tronquee, annee, annees,
    chargement, erreur, filtresActifs, onReinitialiser,
    selection, onSelectionner
}) {
    // ── En-tête de carte : le compte, comme dans la maquette ──────────────────
    const compte = chargement
        ? '…'
        : filtresActifs && total !== totalAnnee
            ? `${total} sur ${totalAnnee}`
            : `${total} étudiant${total > 1 ? 's' : ''}`;

    const clavier = (e, id) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onSelectionner(id);
    };

    // ── Les états où il n'y a rien à montrer ─────────────────────────────────
    let vide = null;
    if (chargement) {
        vide = (
            <div className="etu-vide">
                <i className="fa-solid fa-circle-notch etu-tourne" />
                <div className="etu-vide-titre">Lecture de la liste…</div>
            </div>
        );
    } else if (erreur) {
        vide = (
            <div className="etu-vide">
                <i className="fa-solid fa-triangle-exclamation" />
                <div className="etu-vide-titre">La liste n'a pas pu être lue</div>
                <p className="etu-vide-texte">{erreur}</p>
            </div>
        );
    } else if (!lignes.length && filtresActifs) {
        vide = (
            <div className="etu-vide">
                <i className="fa-solid fa-filter-circle-xmark" />
                <div className="etu-vide-titre">Aucun étudiant ne correspond aux filtres</div>
                <p className="etu-vide-texte">
                    {totalAnnee > 0
                        ? <>L'année <strong>{annee?.libelle}</strong> compte pourtant {totalAnnee} inscrit
                            {totalAnnee > 1 ? 's' : ''}.</>
                        : <>Et cette année n'a aucun inscrit.</>}
                </p>
                <button className="etu-btn etu-btn-neutre" type="button" onClick={onReinitialiser}>
                    <i className="fa-solid fa-rotate-left" /> Retirer les filtres
                </button>
            </div>
        );
    } else if (!lignes.length) {
        // L'année choisie est vide. On nomme celles qui ne le sont pas : c'est la
        // seule façon de distinguer « rien n'a été importé » de « tu regardes la
        // mauvaise année ».
        const peuplees = (annees || []).filter((a) => a.nb_inscrits > 0);
        vide = (
            <div className="etu-vide">
                <i className="fa-solid fa-user-graduate" />
                <div className="etu-vide-titre">
                    Aucun inscrit pour {annee?.libelle || 'cette année'}
                </div>
                <p className="etu-vide-texte">
                    {peuplees.length ? (
                        <>
                            Les étudiants sont sur{' '}
                            {peuplees.map((a, i) => (
                                <React.Fragment key={a.id}>
                                    {i > 0 ? ', ' : ''}
                                    <strong>{a.libelle}</strong> ({a.nb_inscrits})
                                </React.Fragment>
                            ))}
                            {' '}— change l'année dans le filtre ci-dessus.
                        </>
                    ) : (
                        <>
                            Aucun étudiant n'a encore été importé dans ce département. L'import se
                            fait depuis l'onglet <strong>Import APOGEE</strong>.
                        </>
                    )}
                </p>
            </div>
        );
    }

    return (
        <div className="etu-carte etu-carte-liste">
            <div className="etu-carte-entete">
                <i className="fa-solid fa-users" />
                Liste des étudiants
                <span className="etu-carte-compte">{compte}</span>
            </div>

            {vide || (
                <>
                    {tronquee && (
                        <div className="etu-alerte etu-alerte-warning etu-alerte-liste">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span>
                                La liste est plafonnée : seules les {total} premières lignes sont
                                affichées. Affine les filtres.
                            </span>
                        </div>
                    )}
                    <div className="etu-table-cadre-liste">
                        <table className="etu-table etu-table-liste">
                            <thead>
                                <tr>
                                    <th>Étudiant</th>
                                    <th>Formation</th>
                                    <th>Régime</th>
                                    <th>Boursier</th>
                                    <th className="etu-col-statut">Statut</th>
                                    {/* LES DEUX RANGS DE SCODOC — demandés par Bertrand le
                                        2026-09-10, « à droite de Statut ».

                                        ⚠️ L'en-tête tient sur DEUX lignes, et ce n'est pas un
                                        choix esthétique : le panneau ne dispose pas de la
                                        largeur d'un en-tête d'une seule ligne (voir le bloc de
                                        mesures de `.etu-col-rang` dans Etudiants.css).
                                        L'infobulle porte le nom entier — « Rang adm. » seul ne
                                        dit pas de QUELS classés il parle. */}
                                    <th className="etu-col-rang"
                                        title="Rang dans la promotion, au semestre du relevé">
                                        Rang<br />promo
                                    </th>
                                    <th className="etu-col-rang"
                                        title="Rang à l'admission, sur tous les candidats classés">
                                        Rang<br />adm.
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignes.map((l) => {
                                    const st = badgeStatut(l.statut);
                                    const rg = badgeRegime(l.regime_inscription);
                                    const promo = rangSurMax(l.rang_promotion, l.rang_promotion_max);
                                    const admission = rangSurMax(l.classement_recrutement, l.rang_max_recrutement);
                                    return (
                                        <tr
                                            key={l.id}
                                            className={(selection === l.id ? 'etu-ligne-choisie' : '')
                                                + (l.actif === false ? ' etu-ligne-partie' : '')}
                                            onClick={() => onSelectionner(l.id)}
                                            onKeyDown={(e) => clavier(e, l.id)}
                                            tabIndex={0}
                                            aria-selected={selection === l.id}
                                        >
                                            <td>
                                                <div className="etu-identite">
                                                    {/* La photo prend la place des initiales dès
                                                        qu'elle existe.

                                                        ⚠️ Le lot 1.3 annonçait ici que l'onglet
                                                        Photos « remplirait `photo_url` sans
                                                        retoucher cet écran ». C'était faux, et
                                                        mesuré comme tel au lot 1.5 : `photo_url`
                                                        désigne une route qui EXIGE le jeton, et un
                                                        `<img src>` n'en envoie aucun — les
                                                        vignettes restaient vides, sans erreur.
                                                        D'où `PhotoEtudiant`, qui la demande par
                                                        axios et la garde en cache. */}
                                                    <PhotoEtudiant
                                                        chemin={l.photo_url}
                                                        className="etu-avatar etu-avatar-photo"
                                                        alt=""
                                                        secours={(
                                                            <div
                                                                className="etu-avatar"
                                                                style={{ background: teinteAvatar(l.nom, l.prenom) }}
                                                            >
                                                                {initiales(l.nom, l.prenom)}
                                                            </div>
                                                        )}
                                                    />
                                                    <div className="etu-identite-texte">
                                                        {/* Le nom s'écourte en « … » quand le
                                                            panneau est étroit (mesuré à
                                                            l'image) : l'infobulle rend alors
                                                            le nom entier, et le séparateur
                                                            reste là pour élargir. */}
                                                        <div
                                                            className="etu-identite-nom"
                                                            title={`${l.nom} ${l.prenom}`}
                                                        >
                                                            {/* ⚠️ Un étudiant DÉSACTIVÉ reste dans la
                                                                liste (soft delete), et il doit se VOIR :
                                                                une ligne éteinte sans marqueur se lirait
                                                                comme un défaut d'affichage. L'icône porte
                                                                sa raison en infobulle. */}
                                                            {l.actif === false && (
                                                                <i
                                                                    className="fa-solid fa-user-slash etu-marque-partie"
                                                                    title="Étudiant désactivé — sa fiche est conservée, sa photo a été supprimée"
                                                                />
                                                            )}
                                                            {l.nom} {l.prenom}
                                                        </div>
                                                        <div className="etu-identite-num">
                                                            N° {l.code_etudiant || '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {l.formation_nom ? (
                                                    <span className="etu-badge etu-badge-formation"
                                                        title={l.parcours_nom ? 'Parcours ' + l.parcours_nom : undefined}>
                                                        {l.formation_nom}
                                                        {/* 2026-10-29 (E.4) : le parcours de l'année, à côté du groupe. */}
                                                        {l.parcours_code ? ' · ' + l.parcours_code : ''}
                                                    </span>
                                                ) : (
                                                    // La clé étrangère est `SET NULL` : une formation
                                                    // retirée du référentiel laisse l'inscription en
                                                    // place. Elle reste visible, sinon elle serait
                                                    // introuvable tout en existant.
                                                    <span className="etu-sans-formation">
                                                        <i className="fa-solid fa-link-slash" /> à réaffecter
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={'etu-tag-regime ' + rg.classe}>
                                                    {rg.libelle}
                                                </span>
                                            </td>
                                            <td>
                                                {/* « Oui » et non « Boursier » : la colonne
                                                    porte déjà ce mot en en-tête, et le
                                                    répéter coûtait les 40 px qui poussaient
                                                    la colonne Statut hors du cadre. La
                                                    pastille ambre de la maquette est
                                                    conservée, c'est le mot qui part. */}
                                                {l.boursier === true ? (
                                                    <span className="etu-badge etu-badge-boursier">
                                                        <i className="fa-solid fa-euro-sign" /> Oui
                                                    </span>
                                                ) : (
                                                    // « Non » se lit, « — » s'efface : le
                                                    // premier est une information, le second
                                                    // l'absence d'information. Deux teintes,
                                                    // parce que le gris des tirets du module
                                                    // est trop pâle pour porter un mot.
                                                    l.boursier === false
                                                        ? <span className="etu-boursier-non">Non</span>
                                                        : <span className="etu-neutre">—</span>
                                                )}
                                            </td>
                                            <td className="etu-col-statut">
                                                <div className="etu-cellule-statut">
                                                    <span className={'etu-badge ' + st.classe}>
                                                        {st.libelle}
                                                    </span>
                                                    {l.redoublant === true && (
                                                        // Écart E30 : « redoublant » n'est plus une
                                                        // valeur de statut, c'est un fait annuel
                                                        // venu d'APOGEE. Il s'affiche donc À CÔTÉ
                                                        // du statut, jamais à sa place.
                                                        <span className="etu-badge etu-badge-redoublant">
                                                            <i className="fa-solid fa-rotate-left" /> Redoublant
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {/* RANG PROMO. Le SEMESTRE est affiché à côté du
                                                rang, et il n'est pas décoratif : le dénominateur
                                                est écrit ligne par ligne, avec le relevé qui l'a
                                                apportée. Deux étudiants de la même formation
                                                peuvent donc porter « 12 / 20 » et « 12 / 59 »
                                                dans cette liste — sans leur semestre, la colonne
                                                se lirait comme une incohérence. */}
                                            <td className="etu-col-rang">
                                                {promo === null ? (
                                                    <span className="etu-neutre">—</span>
                                                ) : (
                                                    <span
                                                        className="etu-rang"
                                                        title={`Rang ${l.rang_promotion} dans la promotion`
                                                             + (l.rang_promotion_max ? ` sur ${l.rang_promotion_max} classés` : '')
                                                             + (l.rang_promotion_semestre ? ` au semestre ${l.rang_promotion_semestre}` : '')}
                                                    >
                                                        {promo}
                                                        {l.rang_promotion_semestre ? (
                                                            <span className="etu-rang-sem">
                                                                S{l.rang_promotion_semestre}
                                                            </span>
                                                        ) : null}
                                                    </span>
                                                )}
                                            </td>
                                            {/* RANG ADMISSION. Pas de semestre ici : un
                                                recrutement n'en a pas, et son dénominateur ne
                                                descend jamais — il décrit la campagne, la même
                                                pour toute la formation. */}
                                            <td className="etu-col-rang">
                                                {admission === null ? (
                                                    <span className="etu-neutre">—</span>
                                                ) : (
                                                    <span
                                                        className="etu-rang"
                                                        title={`Rang ${l.classement_recrutement} à l'admission`
                                                             + (l.rang_max_recrutement ? ` sur ${l.rang_max_recrutement} candidats classés` : '')}
                                                    >
                                                        {admission}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default EtudiantsListe;
